import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';

import { CopyResult, IIdentity } from '@grid/models';
import { DprActivityData } from '@dpr-details/enums';
import { DprDetailCopyOption } from '@dpr-details/models';
import {
  DprActivityCustomDataGridData,
  DprActivityGridData,
} from '@dpr-details/models/grid';
import { ChangesModel } from '@grid/collections/changes-model';
import { SyncService } from '@shared/services/grid';
import { DprAttachmentsGridData } from '@shared/models/grid';

@Injectable()
export class DprActivityCopyService {
  constructor(private syncService: SyncService) {}

  getOptions(data: DprActivityGridData): DprDetailCopyOption[] {
    const copyOptions: DprDetailCopyOption[] = [];

    if (data.dprSubActivities?.length) {
      copyOptions.push({
        id: DprActivityData.Subactivities,
        name: $localize`:@@dprDetails.dprActivityCopyService.txtSubActivities:Sub Activities`,
        value: true,
      });
    }

    if (data.dprActivityCustomDataList?.length) {
      copyOptions.push({
        id: DprActivityData.CustomData,
        name: $localize`:@@dprDetails.dprActivityCopyService.txtCustomData:Custom Data`,
        value: true,
      });
    }

    if (data.dprActivityAttachments?.length) {
      copyOptions.push({
        id: DprActivityData.Attachments,
        name: $localize`:@@dprDetails.dprActivityCopyService.txtAttachments:Attachments`,
        value: true,
      });
    }

    return copyOptions;
  }

  copy(
    data: DprActivityGridData,
    copyOptions: DprDetailCopyOption[]
  ): DprActivityGridData {
    const result: DprActivityGridData = cloneDeep(data);

    if (!copyOptions?.length) {
      return result;
    }

    const copySubActivities = copyOptions.find(
      (x) => x.id === DprActivityData.Subactivities
    )?.value;

    const copyCustomData = copyOptions.find(
      (x) => x.id === DprActivityData.CustomData
    )?.value;

    const copyAttachments = copyOptions.find(
      (x) => x.id === DprActivityData.Attachments
    )?.value;

    if (copySubActivities) {
      const copyResult = this.copyArray(result.dprSubActivities);
      result.dprSubActivities = copyResult.data;
      result.syncDprSubActivities = copyResult.changes;
    } else {
      result.dprSubActivities = [];
      result.syncDprSubActivities = this.syncService.getDefaultSyncModel();
    }

    if (copyCustomData) {
      const properties: (keyof DprActivityCustomDataGridData)[] = [
        'value',
        'comment',
      ];
      const copyResult = this.copyArray(
        result.dprActivityCustomDataList,
        properties
      );
      result.dprActivityCustomDataList = copyResult.data;
      result.syncDprActivityCustomDataList = copyResult.changes;
    } else {
      result.dprActivityCustomDataList = [];
      result.syncDprActivityCustomDataList =
        this.syncService.getDefaultSyncModel();
    }

    if (copyAttachments) {
      const copyResult = this.copyArray<DprAttachmentsGridData>(
        result.dprActivityAttachments
      );
      result.dprActivityAttachments = copyResult.data;
      result.syncDprActivityAttachments = {
        ...copyResult.changes,
        addedList: copyResult.changes.addedList.map((x) => ({
          ...x,
          id: null,
          // Clear the source attachment's DB id so the backend inserts a new
          // row instead of replaying the original PK (rejected by IDENTITY_INSERT OFF)
          attachmentId: null,
        })),
      };
    } else {
      result.dprActivityAttachments = [];
      result.syncDprActivityAttachments =
        this.syncService.getDefaultSyncModel();
    }

    return result;
  }

  private copyArray<T extends IIdentity>(
    values: T[],
    properties?: (keyof T)[]
  ): CopyResult<T> {
    const data = values.map((x) => ({ ...x, id: uuid() }));

    const changesModel = new ChangesModel<T>();
    changesModel.addValues(data, properties);

    const changes = {
      // we need to create new object to drop the connection between changes and array item
      addedList: changesModel.changed.added.map((x) => ({ ...x })),
      updatedList: [],
      deletedIds: [],
      changedProperties: changesModel.changed.propertiesChanges,
    };

    return { data, changes };
  }
}
